import {ListSubheader, MenuItem} from "@mui/material";
import {Menu} from "@mui/material";
import * as React from "react";
import {useState, useRef} from "react";
import {connect} from "react-redux";
import {Action, bindActionCreators, Dispatch} from "redux";
import {IPlanInfo} from "../../model/ClientsAndPlans.model";

import RootActions from "../root/rootActions";
import HeaderActions from "./header/HeaderActions";
import CprPortfolioActions from "../home-page/CprPortfolioActions";
import {DialogComponent} from "../common/Dialog.component";
import {AON_TRUST_PLAN, orderPlans} from "../../utils/sessionUtil";
import {getTranslation} from "../../utils/translationUtil";

export interface IPlanListPropsFromParent {
    planList: IPlanInfo[];
    currentPlan: IPlanInfo;
}

export type ICPlanListProps = IPlanListPropsFromParent &
    IPlanListPropsFromActions;

export const ENTER_ATC_DIALOG_TITLE = "You are leaving the Aon Investment portal";
export const ENTER_ATC_DIALOG_DESCRIPTION = "You are leaving the Aon Investment Portal " +
    "and going to the Aon Collective Investment Trust portal.";
export const EXIT_ATC_DIALOG_TITLE = "You are leaving the Aon Collective Investment Trust portal";
export const EXIT_ATC_DIALOG_DESCRIPTION = "You are leaving the Aon Collective Investment Trust Portal " +
    "and going to the Aon Investment Portal.";

export const PlanList: React.FunctionComponent<ICPlanListProps> = (props) => {
    const [open, setOpen] = useState(false);
    const [anchorEl, setAnchorEl] = useState<HTMLElement | undefined>(undefined);
    const [selectedPlan, setSelectedPlan] = useState<IPlanInfo | undefined>(undefined);
    const [searchTerm, setSearchTerm] = useState("");
    const searchInputRef = useRef<HTMLInputElement>(null);

    const navigateToPlan = (planInfo: IPlanInfo) => {
        props.rootActions!.resetPlanSettings();
        props.actions!.setCurrentPlan(planInfo);
        props.cprPortfolioActions!.clearFundingLevel();
    };

    const handleATCDialogAgree = () => {
        if (selectedPlan) {
            navigateToPlan(selectedPlan);
        }
    };

    const handleATCDialogCancel = () => {
        setOpen(false);
        setSelectedPlan(undefined);
    };

    const planList: IPlanInfo[] = orderPlans(props.planList);

    if (planList.length === 1) {
        return <div id="plan-list">
            <a className="plan-list__plan-name old-anchor"
               title={props.currentPlan.name} style={{cursor: "default"}}>
                <div className="plan-list__plan-name-container"
                     style={{paddingRight: 16}}>
                    {props.currentPlan.name}
                </div>
            </a>
        </div>;
    }

    const navigateFromAtcPlanToNonAtcPlan = () => {
        return props.currentPlan.name === AON_TRUST_PLAN && selectedPlan?.name !== AON_TRUST_PLAN;
    };

    const navigateToAtcPlanFromNonAtcPlan = () => {
        return selectedPlan?.name === AON_TRUST_PLAN && props.currentPlan.name !== AON_TRUST_PLAN;
    };

    const filteredPlanList = planList.filter((planInfo) =>
        planInfo.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const menuItems = filteredPlanList.map((planInfo, index) =>
        <MenuItem
            className={`client-name-${index + 1} plan-${planInfo.name.replace(/ /g, "_")}`}
            key={planInfo.id}
            value={planInfo.id}
            style={{
                fontFamily: 'Helvetica Now Text", sans-serif',
                fontWeight: "inherit",
            }}
            onClick={() => {
                if (props.currentPlan.name === AON_TRUST_PLAN || planInfo.name === AON_TRUST_PLAN) {
                    setSelectedPlan(planInfo);
                    setOpen(false);
                } else {
                    setSelectedPlan(undefined);
                    navigateToPlan(planInfo);
                }
            }}
            onKeyDownCapture={(e) => {
                if (e.key === "ArrowUp" && index === 0) {
                    e.stopPropagation();
                    e.preventDefault();
                    searchInputRef.current?.focus();
                }
            }}>
            {planInfo.name}
        </MenuItem>);

    const handleClick = (isClick: boolean) => (event: React.KeyboardEvent | React.MouseEvent) => {
        if (isClick || (event as React.KeyboardEvent).key === "Enter" || (event as React.KeyboardEvent).key === " ") {
            event.preventDefault();
            setOpen(true);
            setAnchorEl(event.currentTarget as HTMLElement);
        }
    };

    const renderATCDialog = () => {
        return navigateFromAtcPlanToNonAtcPlan() || navigateToAtcPlanFromNonAtcPlan()
            ? <DialogComponent
                title={navigateToAtcPlanFromNonAtcPlan() ? ENTER_ATC_DIALOG_TITLE : EXIT_ATC_DIALOG_TITLE}
                open={selectedPlan !== undefined}
                noText="Cancel"
                yesText="Agree"
                onNo={handleATCDialogCancel}
                onYes={handleATCDialogAgree}
                description={navigateToAtcPlanFromNonAtcPlan()
                    ? ENTER_ATC_DIALOG_DESCRIPTION : EXIT_ATC_DIALOG_DESCRIPTION}
            />
            : null;
    };

    const getDropdownIconClass = () => {
        return props.currentPlan.name === AON_TRUST_PLAN
            ? "plan-list__icon fa-light fa-angle-down plan-list__icon-atc"
            : "plan-list__icon fa-light fa-angle-down";
    };

    return <div id="plan-list">
        <a className="plan-list__plan-name old-anchor clickable"
           title={props.currentPlan.name}
           aria-label="Plan Selection Dropdown"
           aria-haspopup="listbox"
           aria-expanded={open}
           tabIndex={0}
           onClick={handleClick(true)}
           onKeyDown={handleClick(false)}
        >

            <div className="plan-list__plan-name-container">
                {props.currentPlan.name}
            </div>
            <div className={getDropdownIconClass()} data-testid="plan-list__icon"/>
        </a>

        <Menu
            className="plan-list__menu"
            open={open}
            anchorEl={anchorEl}
            onClose={() => {
                setOpen(false);
                setSearchTerm("");
            }}
            style={{overflowX: "hidden", borderRadius: 0}}
            anchorOrigin={{vertical: "bottom", horizontal: "right"}}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            disableAutoFocusItem
            disableAutoFocus
            slotProps={{
                transition: { onEntered: () => searchInputRef.current?.focus() },
                paper: { style: { maxWidth: 420 } }
            }}
        >
            <ListSubheader className="plan-list__search-header">
                <input
                    ref={searchInputRef}
                    type="text"
                    className="plan-list__search-input"
                    placeholder={getTranslation("header.research", "Search plans...")}
                    aria-label="Search plans"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key !== "ArrowDown" && e.key !== "ArrowUp") {
                            e.stopPropagation();
                        }
                    }}
                    onClick={(e) => e.stopPropagation()}
                    autoComplete="off"
                    data-testid="plan-list__search-input"
                />
            </ListSubheader>
            {menuItems}
        </Menu>
        {renderATCDialog()}
    </div>;
};

export interface IPlanListPropsFromActions {
    actions?: typeof HeaderActions;
    rootActions?: typeof RootActions;
    cprPortfolioActions?: typeof CprPortfolioActions
}

export const mapDispatchToProps = (dispatch: Dispatch<Action<string>>): IPlanListPropsFromActions => {
    return {
        actions: bindActionCreators(HeaderActions, dispatch),
        rootActions: bindActionCreators(RootActions, dispatch),
        cprPortfolioActions: bindActionCreators(CprPortfolioActions, dispatch)
    };
};

export default connect<any, IPlanListPropsFromActions,
    IPlanListPropsFromParent>(null, mapDispatchToProps)(PlanList);
